import { useMemo, useState } from 'react';
import { useFeatureValue, type JSONValue } from '@growthbook/growthbook-react';

import { DEEPLINK_PARAMS } from '@/shared/constants/deeplinkParams';
import { getPlatformOs } from '@/shared/utils/general.util';
import { isNativeApp } from '@/shared/utils/nativeBridge';
import { APP_UPDATE_FLAG } from '../constants/flag';
import { decideAppUpdatePrompt, resolveAppPlatform } from '../lib/appUpdateDecision';
import { parseAppUpdateFlag } from '../lib/appUpdateFlag';
import { readForcedAppUpdateMode } from '../lib/forcedAppUpdate';

const hasLiveViewedFromApp = (): boolean =>
	new URLSearchParams(window.location.search).get(DEEPLINK_PARAMS.VIEWED_FROM) === 'app';

export const useAppUpdatePrompt = () => {
	const flag = parseAppUpdateFlag(useFeatureValue<JSONValue>(APP_UPDATE_FLAG, null));
	const mode = flag?.mode;
	const ios = flag?.ios;
	const android = flag?.android;
	// browser simulation: only the live ?viewed_from=app of this page load, never the stored marker
	const [liveViewedFromApp] = useState(hasLiveViewedFromApp);
	// local development only: forces the prompt for this page load
	const [forcedMode] = useState(() =>
		import.meta.env.DEV ? readForcedAppUpdateMode(window.location.search) : null,
	);

	return useMemo(() => {
		const insideApp = isNativeApp();
		const simulated = !insideApp && liveViewedFromApp;
		return decideAppUpdatePrompt({
			flag: mode ? { mode, ios, android } : null,
			insideApp,
			simulated,
			platform:
				insideApp || simulated || forcedMode
					? resolveAppPlatform(window.HeruApp?.platform, getPlatformOs(), navigator.userAgent)
					: null,
			appVersion: window.HeruApp?.version ?? null,
			forcedMode,
		});
	}, [mode, ios, android, liveViewedFromApp, forcedMode]);
};
